import { hm, fmtUptime, useRaspberryHealth } from "@/hooks/useRaspberryHealth";
import { Cpu, MemoryStick, Thermometer, Activity, HardDrive, Zap, Server, Loader2, AlertTriangle } from "lucide-react";

const Dot = ({ status }) => (
  <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: hm(status).color }} />
);

// Badge global compact "RPi OK/Degraded/Critical"
export const RaspberryBadge = ({ overall, testid = "rpi-badge" }) => {
  const meta = hm(overall);
  return (
    <span data-testid={testid} className="flex items-center gap-1.5 rounded-full border px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider"
      style={{ borderColor: meta.color + "66", color: meta.color, background: meta.color + "10" }}>
      <Server size={10} /> RPi · {meta.label}
    </span>
  );
};

// Métrique unité (valeur + statut coloré)
const Metric = ({ icon: Icon, label, value, unit, status, testid }) => (
  <div data-testid={testid} className="flex items-center gap-1.5 rounded-sm border border-white/10 bg-black/30 px-2 py-1">
    <Icon size={12} style={{ color: hm(status).color }} />
    <div className="flex flex-col leading-none">
      <span className="font-mono text-[8px] uppercase tracking-wider text-[#A3A3A3]">{label}</span>
      <span className="font-mono text-[11px] font-bold" style={{ color: hm(status).color }}>
        {value != null ? value : "—"}{value != null && unit ? unit : ""}
      </span>
    </div>
  </div>
);

// Bloc "Santé embarquée" complet pour le cockpit
export const EmbeddedHealthPanel = ({ health, loading, error }) => {
  const overall = health?.overall || "unknown";
  const unreachable = health && health.reachable === false;
  return (
    <div data-testid="rpi-health-panel" className="bg-[#141414] border border-white/10 rounded-sm">
      <div className="px-3 py-2 flex items-center gap-2 border-b border-white/10">
        <Server size={12} className="text-[#A3A3A3]" />
        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#A3A3A3]">Santé embarquée</span>
        <div className="ml-auto flex items-center gap-2">
          {loading && <Loader2 size={11} className="animate-spin text-[#3B82F6]" />}
          <RaspberryBadge overall={overall} />
        </div>
      </div>
      {unreachable ? (
        <div data-testid="rpi-health-unreachable" className="p-3 flex items-center gap-2 font-mono text-[10px] text-[#F59E0B]">
          <AlertTriangle size={13} /> Raspberry injoignable {health?.not_deployed ? "(endpoint santé non déployé — Infra API v2.6+)" : ""}
        </div>
      ) : (
        <div className="p-2 space-y-2">
          <div className="grid grid-cols-3 gap-1.5">
            <Metric icon={Cpu} label="CPU" value={health?.cpu?.value} unit="%" status={health?.cpu?.status} testid="rpi-cpu" />
            <Metric icon={MemoryStick} label="RAM" value={health?.ram?.value} unit="%" status={health?.ram?.status} testid="rpi-ram" />
            <Metric icon={Thermometer} label="Temp" value={health?.temp?.value} unit="°C" status={health?.temp?.status} testid="rpi-temp" />
          </div>
          <div className="flex flex-wrap items-center gap-1.5 font-mono text-[9px]">
            <span data-testid="rpi-throttle" className="flex items-center gap-1 rounded-full border px-2 py-0.5 uppercase tracking-wider"
              style={{ borderColor: hm(health?.throttling?.status).color + "66", color: hm(health?.throttling?.status).color }}>
              <Zap size={10} /> {health?.throttling?.label || "—"}
            </span>
            <span data-testid="rpi-agent" className="flex items-center gap-1 rounded-full border border-white/10 px-2 py-0.5 uppercase tracking-wider text-[#A3A3A3]">
              <Dot status={health?.services?.agent_status} /> Agent {health?.services?.agent || "—"}
            </span>
            <span data-testid="rpi-mediamtx" className="flex items-center gap-1 rounded-full border border-white/10 px-2 py-0.5 uppercase tracking-wider text-[#A3A3A3]">
              <Dot status={health?.services?.mediamtx_status} /> MediaMTX {health?.services?.mediamtx || "—"}
            </span>
            <span className="flex items-center gap-1 text-[#A3A3A3]"><Activity size={10} /> Load {health?.load?.[0] != null ? health.load[0] : "—"}</span>
            {health?.disk_percent != null && <span className="flex items-center gap-1 text-[#A3A3A3]"><HardDrive size={10} /> Disk {health.disk_percent}%</span>}
            <span className="text-[#525252]">Uptime {fmtUptime(health?.uptime_s)}</span>
            {health?.agent_version && <span className="text-[#525252]">v{health.agent_version}</span>}
          </div>
        </div>
      )}
    </div>
  );
};

// Résumé compact pour la popup carte (fetch autonome à l'ouverture)
export const DroneHealthMini = ({ droneId }) => {
  const { health, loading } = useRaspberryHealth(droneId, { interval: 10000 });
  if (loading && !health) return <div className="font-mono text-[10px] text-[#A3A3A3] mt-1">Santé RPi…</div>;
  if (!health || health.reachable === false) {
    return <div data-testid="map-rpi-unreachable" className="font-mono text-[10px] text-[#F59E0B] mt-1">RPi: injoignable</div>;
  }
  return (
    <div data-testid="map-rpi-health" className="mt-1.5 pt-1.5 border-t border-black/10">
      <div className="flex items-center gap-1.5 mb-1">
        <Dot status={health.overall} /><span className="font-bold">Raspberry: {hm(health.overall).label}</span>
      </div>
      <div className="flex items-center gap-2">
        <span style={{ color: hm(health.cpu?.status).color }}>CPU {health.cpu?.value ?? "—"}%</span>
        <span style={{ color: hm(health.ram?.status).color }}>RAM {health.ram?.value ?? "—"}%</span>
        <span style={{ color: hm(health.temp?.status).color }}>{health.temp?.value ?? "—"}°C</span>
      </div>
    </div>
  );
};
